/*
 *
 * Chatbot
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 16-Apr-2025
 */
import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Capacitor } from '@capacitor/core'
import {
  getChatbotScriptDataAttributes,
  getChatbotScriptBasedOnENV,
  openExternalUrl
} from './getChatbotScriptBasedOnENV'
import { observeChatbotGatewayActivity } from './observeChatbotGatewayActivity'
import './Chatbot.scss'

const Chatbot = ({ actions, isMounted, onBack, isSessionPopupOpen }) => {
  // Returns the chatbot script URL and sets portalToken + logged.
  const scriptChatbotUrl = useMemo(
    () => getChatbotScriptBasedOnENV(actions.doSessionActivity),
    [actions.doSessionActivity]
  )
  const scriptRef = useRef(null)
  const containerRef = useRef(null)
  const widgetRef = useRef(null)
  const [widgetVersion, setWidgetVersion] = useState(0)
  const screenActiveRef = useRef(false)
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  const mountChatbot = useCallback(() => {
    if (!screenActiveRef.current || !containerRef.current || widgetRef.current) return
    const isNative = Capacitor.isNativePlatform()
    const widget = window.NexiPortalChat.mount(isNative ? containerRef.current : document.body,
      isNative
        ? { channelType: 'APP', onClose: () => onBackRef.current() }
        : {
            channelType: 'SITO',
            onClose: () => {
              if (widgetRef.current === widget) setWidgetVersion(version => version + 1)
            }
          }
    )
    widgetRef.current = widget
  }, [])

  const callChatbotScript = useCallback(() => {
    if (window.NexiPortalChat?.mount) {
      mountChatbot()
      actions.doChatBotMounted(true)
      return
    }

    const script = document.createElement('script')

    if (!Capacitor.isNativePlatform() && window.renderExternalComponent) {
      window.renderExternalComponent(
        () => { },
        null,
        () => actions.doChatBotMounted(false),
        openExternalUrl
      )
    } else {
      script.src = `${scriptChatbotUrl}?v=${Date.now()}`
      Object.entries({
        ...getChatbotScriptDataAttributes(),
        'data-environment': window.sessionStorage.getItem('env') || 'DEV'
      }).forEach(([name, value]) => script.setAttribute(name, value))
      script.onload = () => {
        if (!Capacitor.isNativePlatform() && window.renderExternalComponent) {
          window.renderExternalComponent(
            () => { },
            null,
            () => actions.doChatBotMounted(false),
            openExternalUrl
          )
        }
        if (window.NexiPortalChat?.mount) {
          if (!screenActiveRef.current && !Capacitor.isNativePlatform()) {
            // SITO auto-mounts on load, even if the owning page has already closed.
            window.NexiPortalChat.mount(document.body, { channelType: 'SITO' }).destroy()
          } else {
            mountChatbot()
          }
        }
      }
    }

    scriptRef.current = script
    document.body.appendChild(script)
    actions.doChatBotMounted(true)
  }, [scriptChatbotUrl, actions, mountChatbot])

  useEffect(() => {
    if (isSessionPopupOpen) return undefined

    /*
      1) Calculate and set "env" in sessionStorage
         based on the DEPLOY_ENV_NAME build variable.
    */
    const buildEnv = process.env.DEPLOY_ENV_NAME || process.env.ENVIRONMENT || 'DEVELOP'
    let env = 'DEV'

    if (buildEnv === 'PRODUCTION') {
      env = 'PROD'
    } else if (buildEnv === 'STAGING') {
      env = 'STG'
    } else {
      env = 'DEV'
    }

    window.sessionStorage.setItem('env', env)

    /*
      2) Set portalName and openLinkWithCordova
         based on the current platform.
    */
    if (Capacitor.isNativePlatform()) {
      window.sessionStorage.setItem('portalName', 'NEW_NEXI_BUSINESS_APP')
      window.sessionStorage.setItem(
        'openLinkWithCordova',
        (url) => openExternalUrl(url)
      )
    } else {
      window.sessionStorage.setItem('portalName', 'NEW_NEXI_BUSINESS_WEB')
    }

    /*
      3) If the chatbot is not mounted yet,
         load the script after 300ms.
    */
    if (!isMounted) {
      const timer = setTimeout(callChatbotScript, 300)
      return () => clearTimeout(timer)
    }
  }, [isMounted, callChatbotScript, isSessionPopupOpen])

  useEffect(() => {
    if (isSessionPopupOpen) return undefined
    return observeChatbotGatewayActivity(
      actions.doSessionActivity, getChatbotScriptDataAttributes()['data-proxy-base-url']
    )
  }, [actions.doSessionActivity, isSessionPopupOpen])

  useEffect(() => {
    if (isSessionPopupOpen) {
      if (Capacitor.isNativePlatform()) onBackRef.current()
      return undefined
    }
    screenActiveRef.current = true
    if (window.NexiPortalChat?.mount) {
      mountChatbot()
    }

    return () => {
      screenActiveRef.current = false
      widgetRef.current?.destroy()
      widgetRef.current = null
    }
  }, [mountChatbot, isSessionPopupOpen, widgetVersion])

  // Cleanup on unmount.
  useEffect(
    () => () => {
      if (scriptRef.current) {
        scriptRef.current.remove()
      }
      actions.doChatBotMounted(false)
    },
    [actions]
  )

  return <div id='chatbotApp' ref={containerRef} />
}

Chatbot.propTypes = {
  actions: PropTypes.shape({
    doChatBotMounted: PropTypes.func.isRequired,
    doSessionActivity: PropTypes.func.isRequired
  }).isRequired,
  isMounted: PropTypes.bool,
  isSessionPopupOpen: PropTypes.bool,
  onBack: PropTypes.func
}

Chatbot.defaultProps = {
  isMounted: false,
  isSessionPopupOpen: false,
  onBack: () => { }
}

export default Chatbot
